import { useEffect, type ReactNode } from 'react';
import { Button } from '@gonka-wallet/ui-tokens';

export interface ConfirmDialogProps {
  readonly open: boolean;
  readonly title: ReactNode;
  readonly body?: ReactNode;
  readonly confirmLabel: string;
  readonly cancelLabel: string;
  /** When true, uses the `danger` Button variant + neg micro-label. */
  readonly destructive?: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

/**
 * Minimalist confirmation dialog matching the new visual language.
 *
 * Centered card on a dimmed backdrop. Tap outside or press Escape to
 * cancel. Two stacked CTAs — primary/danger confirm + tertiary cancel.
 * Used for destructive actions (remove wallet, remove watched address,
 * wipe, etc.) where an inline confirm would be too easy to hit by
 * mistake.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  destructive = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-5"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-surface-1 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-[17px] font-semibold leading-tight text-text-primary">
          {title}
        </div>
        {body && (
          <div className="mt-2 text-[13px] leading-relaxed text-text-secondary">
            {body}
          </div>
        )}
        <div className="mt-5 flex flex-col gap-2">
          <Button
            variant={destructive ? 'danger' : 'primary'}
            fullWidth
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
          <Button variant="tertiary" fullWidth onClick={onCancel}>
            {cancelLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
